import { forwardRef, type ReactNode } from 'react'
import { ChevronDown } from '../../icons'
import { cn } from '../../lib/cn'
import { Button, type ButtonProps } from '../button/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../dropdown-menu/dropdown-menu'

export interface SplitButtonProps extends Omit<ButtonProps, 'children'> {
  /** The primary half: what the button says, or the element it renders as `asChild`. */
  action: ReactNode
  /** Accessible name for the chevron half — what the menu holds. */
  menuLabel: string
  /**
   * Disables the chevron half. Independent of `disabled`, which disables the
   * action half only: an action that cannot be taken right now does not make
   * its alternatives unreachable, and that is usually the point of them.
   */
  menuDisabled?: boolean
  /** Which edge the menu aligns to. Defaults to the button's end. */
  align?: 'start' | 'center' | 'end'
  /** The menu's items. */
  children: ReactNode
}

/**
 * One action and the other ways to take it, as a single control.
 *
 * The chevron is joined to the button rather than set beside it because a
 * detached chevron — or worse, a detached `…` — reads as a second control of
 * equal weight, which is the thing this exists to avoid: the row says one
 * action, and the menu says "or, instead of that".
 *
 * That also fixes what belongs in it. The items are *alternatives to the
 * button*: publish by browser instead of by API, add media from Files instead
 * of from disk. Unrelated extras belong in a standalone overflow menu, which
 * is a different control and looks like one.
 *
 * @example
 * ```tsx
 * <SplitButton variant='primary' action='Publish' onClick={publish} menuLabel='Other ways to publish'>
 *   <DropdownMenuItem onSelect={openBrowser}>Open the browser assistant</DropdownMenuItem>
 * </SplitButton>
 * ```
 */
export const SplitButton = forwardRef<HTMLButtonElement, SplitButtonProps>(
  (
    {
      action,
      menuLabel,
      menuDisabled,
      align = 'end',
      children,
      className,
      variant,
      size,
      disabled,
      ...props
    },
    ref
  ) => (
    <div className={cn('inline-flex items-center', className)}>
      <Button
        ref={ref}
        variant={variant}
        size={size}
        disabled={disabled}
        className='rounded-r-none'
        {...props}
      >
        {action}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type='button'
            variant={variant}
            size={size}
            aria-label={menuLabel}
            disabled={menuDisabled}
            /* The halves share one border rather than drawing two: `-ml-px`
               pulls the second onto the first, and the divider is the seam
               that remains. */
            className='-ml-px rounded-l-none border-l border-l-[var(--border-strong)] px-[var(--s-4)]'
          >
            <ChevronDown aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align}>{children}</DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
)

SplitButton.displayName = 'SplitButton'
